import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { clsx } from 'clsx';
import * as React from 'react';
const SkeletonContext = React.createContext({ loading: false });
export function SkeletonProvider({ loading, children }) {
    const parent = React.useContext(SkeletonContext);
    const value = React.useMemo(() => ({ loading: parent.loading || loading }), [parent.loading, loading]);
    return _jsx(SkeletonContext.Provider, { value: value, children: children });
}
export function useSkeleton() {
    return React.useContext(SkeletonContext);
}
function buildSkeletonStyle(values) {
    return Object.entries(values).reduce((acc, [key, value]) => {
        if (value) {
            acc[key] = value;
        }
        return acc;
    }, {});
}
function shouldRenderSkeleton(contextLoading, overrideLoading, hasChildren) {
    if (overrideLoading !== undefined) {
        return overrideLoading;
    }
    return hasChildren ? contextLoading : true;
}
/**
 * SkeletonBlock - either renders a shimmer block or reveals children.
 *
 * - Without children: always renders the skeleton block (backward compatible).
 * - With children: renders the skeleton block only when SkeletonProvider loading is true
 *   or when the local `loading` prop is true.
 */
export function SkeletonBlock({ className, children, loading, width, height, radius, as = 'div', }) {
    const { loading: contextLoading } = useSkeleton();
    const renderSkeleton = shouldRenderSkeleton(contextLoading, loading, children !== undefined);
    if (!renderSkeleton) {
        return _jsx(_Fragment, { children: children });
    }
    const Component = as;
    const style = buildSkeletonStyle({
        '--skeleton-width': width,
        '--skeleton-height': height,
        '--skeleton-radius': radius,
    });
    return (_jsx(Component, { className: clsx('ui-skeleton', 'ui-skeleton--block', 'skeleton-block', className), style: style, "aria-hidden": "true" }));
}
export function SkeletonCircle({ className, children, loading, size, as = 'div', }) {
    const { loading: contextLoading } = useSkeleton();
    const renderSkeleton = shouldRenderSkeleton(contextLoading, loading, children !== undefined);
    if (!renderSkeleton) {
        return _jsx(_Fragment, { children: children });
    }
    const Component = as;
    const style = buildSkeletonStyle({
        '--skeleton-width': size,
        '--skeleton-height': size,
    });
    return (_jsx(Component, { className: clsx('ui-skeleton', 'ui-skeleton--circle', className), style: style, "aria-hidden": "true" }));
}
export function SkeletonText({ className, children, loading, width, lastLineWidth, lines = 1, lineHeight, as = 'div', }) {
    const { loading: contextLoading } = useSkeleton();
    const renderSkeleton = shouldRenderSkeleton(contextLoading, loading, children !== undefined);
    if (!renderSkeleton) {
        return _jsx(_Fragment, { children: children });
    }
    const Component = as;
    const safeLines = Math.max(1, lines);
    const style = buildSkeletonStyle({
        '--skeleton-width': width,
        '--skeleton-line-height': lineHeight,
        '--skeleton-last-line-width': lastLineWidth,
    });
    return (_jsx(Component, { className: clsx('ui-skeleton-text', className), style: style, "aria-hidden": "true", children: Array.from({ length: safeLines }).map((_, index) => (_jsx("span", { className: "ui-skeleton ui-skeleton--text-line" }, index))) }));
}
//# sourceMappingURL=Skeleton.js.map